// Чистая логика блока фото-карточек работ (ChatBlock 'gallery'): фильтр и
// потолок. DOM здесь нет намеренно — модуль тестируется в изоляции
// (gallery.test.mjs), как theme.ts.
// import type (а не обычный import): под `node --test --experimental-strip-types`
// тип стирается вместе со строкой импорта — тесту не нужен рантайм api.ts.
import type { GalleryCard } from './api';

/** Потолок карточек на блок — зеркало серверного CatalogEntity::MAX_PER_ASSISTANT.
 *  Клиентская страховка §12: панель виджета не должна рисовать ленту без конца,
 *  даже если наружу когда-нибудь приедет больше. */
export const GALLERY_MAX_CARDS = 12;

/** Карточки к отрисовке: без картинки карточка бессмысленна (пустой src даёт
 *  «битое фото» на чужом сайте), заголовок обязателен — это и alt изображения,
 *  и доступное имя ссылки. Лишние сверх потолка отбрасываем. */
export function galleryCards(items: GalleryCard[]): GalleryCard[] {
  return items.filter((card) => Boolean(card?.image) && Boolean(card?.title)).slice(0, GALLERY_MAX_CARDS);
}

/** Доступное имя карточки-ссылки: «заголовок · мета» (мета необязательна).
 *  Ставится на <a> aria-label, чтобы имя не задваивалось с alt картинки. */
export function galleryCardLabel(card: GalleryCard): string {
  return card.meta ? `${card.title} · ${card.meta}` : card.title;
}

// Цена = ЧИСЛО рядом с валютой. Просто «руб» искать нельзя: подпись владельца
// свободная, и «сруб 6×8» или «без рубероида» уехали бы из меты в строку цены
// (замечание Codex). «от 670 000» без знака валюты — тоже цена, так пишут вилку,
// но только с большим числом: «от 10 дней» — это срок. Границу слова (\b) не
// используем: в JS она про латиницу и с кириллицей не срабатывает вовсе.
const CURRENCY = /\d[\d\s\u00a0\u202f]*(?:₽|\$|€|руб)|(?:^|\s)от\s+\d[\d\s]{3,}/i;
const META_SEPARATOR = '·';

/** Подпись карточки, разложенная на цену и всё остальное.
 *
 *  Владелец пишет мету одной строкой через «·» («99 м² · 250 мм · М300 ·
 *  17 дней · Калининград · 970 000 ₽»). В карточке шириной 220px она
 *  разваливается на три-четыре строки мелкого серого текста, и цена — главный
 *  для клиента факт — тонет последней. Здесь она достаётся КАК ЕСТЬ, чтобы
 *  показать её отдельной строкой: ничего не считаем, не округляем и не
 *  переписываем — только меняем место показа.
 *
 *  @param meta подпись карточки; пусто — обе части пустые.
 *  @return price — сегмент с валютой (null, если такого нет), rest — остальные
 *    сегменты в прежнем порядке. */
export function splitPrice(meta?: string | null): { price: string | null; rest: string } {
  const parts = (meta ?? '')
    .split(META_SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  // Последний с валютой, а не первый: «М300, ЭППС» иногда несёт «от», а цена
  // у владельца стоит в конце строки.
  const index = parts.map((part) => CURRENCY.test(part)).lastIndexOf(true);
  if (index === -1) return { price: null, rest: parts.join(` ${META_SEPARATOR} `) };

  const [price] = parts.splice(index, 1);
  return { price, rest: parts.join(` ${META_SEPARATOR} `) };
}
